// ============================================
// Path: /frontend/src/components/VehiclesSearchFilter.jsx
// Purpose: Search filter for vehicles page (make, model, location)
// ============================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Search } from 'lucide-react';
import { counties } from '../lib/locations';
import { makes } from '../lib/vehicles';
import React from 'react';

export function VehiclesSearchFilter() {
  console.log('[VehiclesSearchFilter] Component mounted');

  const navigate = useNavigate();
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [location, setLocation] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    console.log('[VehiclesSearchFilter] Submitting search:', { make, model, location });

    const params = new URLSearchParams();
    params.append('category', 'vehicle');
    
    if (make) {
      params.append('make', make);
      console.log('[VehiclesSearchFilter] Added make filter:', make);
    }
    if (model) {
      params.append('model', model);
      console.log('[VehiclesSearchFilter] Added model filter:', model);
    }
    if (location) {
      const [county, subCounty] = location.split('/');
      if (county) {
        params.append('county', county);
        console.log('[VehiclesSearchFilter] Added county filter:', county);
      }
      if (subCounty) {
        params.append('subCounty', subCounty);
        console.log('[VehiclesSearchFilter] Added subCounty filter:', subCounty);
      }
    }

    const searchUrl = `/search?${params.toString()}`;
    console.log('[VehiclesSearchFilter] Navigating to:', searchUrl);
    navigate(searchUrl);
  };

  const handleMakeChange = (value) => {
    console.log('[VehiclesSearchFilter] Make changed to:', value);
    setMake(value);
    setModel('');
  };

  const availableModels = makes.find((m) => m.name === make)?.models || [];
  console.log('[VehiclesSearchFilter] Available models for', make, ':', availableModels.length);

  return (
    <Card className="bg-muted/40">
      <CardContent className="p-3 sm:p-4 md:p-6">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3 sm:gap-4 items-end">
          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="make" className="text-xs sm:text-sm">Make</Label>
            <Select onValueChange={handleMakeChange} value={make}>
              <SelectTrigger id="make" className="text-xs sm:text-sm h-9 sm:h-10">
                <SelectValue placeholder="Any Make" />
              </SelectTrigger>
              <SelectContent>
                {makes.map((m) => (
                  <SelectItem key={m.name} value={m.name} className="text-xs sm:text-sm">
                    {m.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="model" className="text-xs sm:text-sm">Model</Label>
            <Select onValueChange={setModel} value={model} disabled={!make}>
              <SelectTrigger id="model" className="text-xs sm:text-sm h-9 sm:h-10">
                <SelectValue placeholder="Any Model" />
              </SelectTrigger>
              <SelectContent>
                {availableModels.map((m) => (
                  <SelectItem key={m} value={m} className="text-xs sm:text-sm">
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5 sm:space-y-2">
            <Label htmlFor="location" className="text-xs sm:text-sm">Location</Label>
            <Select onValueChange={setLocation} value={location}>
              <SelectTrigger id="location" className="text-xs sm:text-sm h-9 sm:h-10">
                <SelectValue placeholder="Any Location" />
              </SelectTrigger>
              <SelectContent>
                {counties.map((c) => (
                  <React.Fragment key={c.name}>
                    <SelectItem value={c.name} className="text-xs sm:text-sm">{c.name}</SelectItem>
                    {c.subCounties.map((sc) => (
                      <SelectItem key={`${c.name}/${sc}`} value={`${c.name}/${sc}`} className="text-xs sm:text-sm">
                        <span className="ml-4">- {sc}</span>
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end">
            <Button type="submit" size="default" className="w-full text-xs sm:text-sm h-9 sm:h-10">
              <Search className="mr-2 w-4 h-4" />
              Find Vehicles
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}